import * as React from "react";
import {inject, observer} from "mobx-react";
import styled, {css} from "styled-components";
import type {Field, IFieldAdapter} from "../../../../data/models/field/Field";
import type {IViewColumn} from "../../../../data/models/ViewUtils";
import {XyiconFeature} from "../../../../generated/api/base";
import type {Navigation} from "../../../../Navigation";
import type {Pointer} from "../../../../utils/interaction/Pointer";
import type {AppState} from "../../../../data/state/AppState";
import type {Xyicon} from "../../../../data/models/Xyicon";
import type {IModel} from "../../../../data/models/Model";
import {BoundaryUtils} from "../../../../data/models/BoundaryUtils";
import LocationPinIcon from "../../icons/location-pin.svg?react";
import EmbedIcon from "../../icons/embed.svg?react";
import OpenPortfolioIcon from "../../icons/arrow-up-right-from-square.svg?react";
import {ReactUtils} from "../../../utils/ReactUtils";
import {PointerDetectorReact} from "../../../interaction/PointerDetectorReact";
import {CheckboxInputStyled, CheckboxInputV5} from "../../details/datatypes/CheckboxInputV5";
import type {Boundary} from "../../../../data/models/Boundary";
import {minHeaderWidth, minIconHeaderWidth, stickyReportTableRowIconWidth, stickyTableRowIconWidth} from "../../../widgets/table/TableConstants";
import {Functions} from "../../../../utils/function/Functions";
import {BoundarySpaceMapSelectorV5} from "../../spaceeditor/BoundarySpaceMapSelectorV5";
import {colorPalette} from "../../styles/colorPalette";
import {baseDistance, ELLIPSIS, FlexCenterStyle, fontSize, radius} from "../../styles/styles";
import {DateTimeInputStyled} from "../../input/datetime/DateTimeInputV5";
import {NavigationEnum} from "../../../../Enums";
import type {Catalog} from "../../../../data/models/Catalog";
import type {Report} from "../../../../data/models/Report";
import StarIcon from "../../icons/star.svg?react";
import StarFillIcon from "../../icons/star-fill.svg?react";
import PlayIcon from "../../icons/play.svg?react";
import {InitialsStyled} from "../../widgets/InitialsV5";
import {getFieldTypeIcons} from "../../settings/modules/abstract/SettingsUtils";
import {MultiSelectInputStyled} from "../../details/datatypes/multi/MultiSelect.styled";
import {openWorkspaceFromPortfolioModule} from "../../modules/ActionBarUtils";
import {getReportIcon} from "../../../../data/models/ReportUtils";
import {selectWorkspaceViewTabForFeature} from "../../topbar/ViewTabsCommon";
import {TableCellV5} from "./TableCellV5";

export type SpotlightColorMode = "row" | "field" | "both";
export type TableFieldSpotlight = {readonly color: string | null} | null;

interface ITableRowProps<T> {
	readonly id: string | number;
	readonly item: T;
	readonly fields?: IFieldAdapter[];
	readonly getFieldsArray?: (item: T, index?: number, headers?: readonly Partial<IViewColumn>[]) => IFieldAdapter[];
	readonly headers: Partial<IViewColumn>[];
	readonly index: number;
	readonly numberOfItemsOnPage: number;
	readonly selected: boolean;
	readonly focused?: boolean;
	readonly showCheckboxColumn: boolean;
	readonly quickLinksActive: boolean;
	readonly feature?: XyiconFeature;
	readonly navigation?: Navigation;
	readonly tableRowIcon?: boolean;
	readonly isCellContentsWrappingOn?: boolean;
	readonly getItemsToUpdate: (item: T) => IModel[];
	readonly hasMultipleFocusedItems: boolean;
	readonly isSimpleTable?: boolean;
	readonly appState?: AppState;
	readonly spotlightColor?: string | null;
	readonly colorApplicationMode?: SpotlightColorMode;
	readonly fieldSpotlights?: readonly TableFieldSpotlight[];

	readonly onFocus?: (event: React.MouseEvent, item: T) => void;
	readonly onDoubleClick?: (item: T) => void;
	readonly onCheckboxRowClick?: (event: React.MouseEvent, item: T) => void;
	readonly showQuickLinks: (e: React.MouseEvent, item: T, index: number) => void;
	readonly onMouseMove?: (pointer: Pointer, row: TableRowV5<T>) => void;
	readonly scrollTableToShowFullCell?: (e: React.MouseEvent) => void;
	readonly runReport?: (reports: Report[]) => Promise<void>;
}

type Highlight = "Top" | "Middle" | "Bottom" | "None";

interface ITableRowState {
	isBoundarySelectorOpen: boolean;
	highlight: Highlight; // Top/Bottom: Shows an empty row with dashed border on top/bottom of this one. Middle: show dashed border with blue background on this row
	isGreyedOut: boolean;
	isNotAllowed: boolean;
	dragNdrop: boolean;
}

@inject("app")
@inject("appState")
@inject("transport")
@inject("navigation")
@observer
export class TableRowV5<T> extends React.Component<ITableRowProps<T>, ITableRowState> {
	private readonly _hoverClassName = "hoverDashed";
	private readonly _tdRef = React.createRef<HTMLTableCellElement>();
	private readonly _trRef: React.RefObject<HTMLTableRowElement> = React.createRef<HTMLTableRowElement>();
	private _committedEditingProps: Pick<ITableRowProps<T>, "item" | "getItemsToUpdate">;

	constructor(props: ITableRowProps<T>) {
		super(props);
		this._committedEditingProps = props;
		this.state = {
			isBoundarySelectorOpen: false,
			highlight: "None",
			isGreyedOut: false,
			isNotAllowed: false,
			dragNdrop: false,
		};
	}

	public override componentDidMount() {
		this.commitEditingProps();
	}

	public override componentDidUpdate() {
		this.commitEditingProps();
	}

	private commitEditingProps() {
		const {item, getItemsToUpdate} = this.props;
		this._committedEditingProps = {item, getItemsToUpdate};
	}

	private readonly getItemsToUpdate = (): IModel[] => {
		// Selection changes only affect edit targets. Read the last committed props
		// when an edit is saved so unchanged cell editors can retain their render.
		const {item, getItemsToUpdate} = this._committedEditingProps;
		return getItemsToUpdate(item);
	};

	private readonly onMouseDown = (event: React.MouseEvent) => {
		if (event.shiftKey) {
			event.preventDefault();
		}
	};

	private readonly onMouseMove = (pointer: Pointer) => {
		this.props.onMouseMove?.(pointer, this);
	};

	private readonly onClick = (event: React.MouseEvent) => {
		// Ignore double click events
		if (event.detail === 1) {
			this.props.onFocus(event, this.props.item);
		}
	};

	private readonly onDoubleClick = (event: React.MouseEvent) => {
		const item: any = this.props.item;

		if (!item.isUnplotted) {
			this.props.onDoubleClick?.(item);
		}
	};

	private readonly onShowBoundarySpaceMaps = () => {
		this.setState({
			isBoundarySelectorOpen: true,
		});
	};

	private readonly onHideBoundarySpaceMaps = () => {
		this.setState({
			isBoundarySelectorOpen: false,
		});
	};

	private get isUnplotted() {
		const item = this.props.item as unknown as Xyicon;

		return item.isUnplotted;
	}

	private readonly onItemActionClick = (event: React.MouseEvent) => {
		event.stopPropagation();
		const {item, feature, appState, navigation} = this.props;

		if (!this.isUnplotted) {
			if ((item as unknown as IModel).ownFeature === XyiconFeature.Space) {
				selectWorkspaceViewTabForFeature(appState, XyiconFeature.SpaceEditor, true);
				navigation.goApp({
					mainNav: NavigationEnum.SPACE,
					subNavs: [(item as unknown as IModel).refId],
				});
			} else if ((item as unknown as IModel).ownFeature === XyiconFeature.Xyicon && feature === XyiconFeature.Xyicon) {
				appState.actions.navigateToSpaceItem(item as unknown as Xyicon, true);
			} else if ((item as unknown as IModel).ownFeature === XyiconFeature.Boundary && feature === XyiconFeature.Boundary) {
				if (BoundaryUtils.doesHaveMultipleSpaceMaps(item as unknown as IModel)) {
					this.onShowBoundarySpaceMaps();
				} else {
					appState.actions.navigateToSpaceItem(item as unknown as Xyicon, true);
				}
			}
		}
	};

	private getIcon() {
		return <LocationPinIcon />;
	}

	private readonly onOpenContextMenu = (e: React.MouseEvent) => {
		const {item, index, showQuickLinks} = this.props;

		if (!this.isUnplotted) {
			showQuickLinks(e, item, index);
		} else {
			e.preventDefault();
		}
	};

	private readonly onFavoriteClick = () => {
		return this.setFavorite(true);
	};

	private readonly onUnFavoriteClick = () => {
		return this.setFavorite(false);
	};

	private setFavorite(value: boolean) {
		(this.props.item as Catalog).setFavorite(value);
	}

	private onRunReport = (e: React.MouseEvent, report: Report) => {
		e.stopPropagation();
		this.props.runReport([report]);
	};

	private getRowIcon() {
		const {quickLinksActive, isSimpleTable, appState} = this.props;
		const item = this.props.item as unknown as IModel;

		if (isSimpleTable) {
			return <td className="tableRowIcon">{getFieldTypeIcons(this.props.item as Field)}</td>;
		}

		if (this.state.highlight === "Middle") {
			return (
				<td className="tableRowIcon embed">
					<EmbedIconWrapperStyled>
						<EmbedIcon />
					</EmbedIconWrapperStyled>
				</td>
			);
		} else if ([XyiconFeature.Boundary, XyiconFeature.Xyicon].includes(item.ownFeature)) {
			const spaceMaybe = (item as Xyicon | Boundary).space || this.isUnplotted;

			if (spaceMaybe) {
				return (
					<td
						className={ReactUtils.cls("tableRowIcon link", {active: quickLinksActive, disabled: this.isUnplotted})}
						onClick={this.onItemActionClick}
						onContextMenu={this.onOpenContextMenu}
						title={
							item.ownFeature === XyiconFeature.Xyicon
								? this.isUnplotted
									? "Cannot open unplotted xyicons in the Space Editor"
									: "Open in Space Editor"
								: ""
						}
					>
						{this.getIcon()}
					</td>
				);
			}
		} else if (item.ownFeature === XyiconFeature.XyiconCatalog) {
			const catalog = item as Catalog;

			return catalog.isFavorite ? (
				<td
					className="tableRowIcon favoriteCatalogItems link button"
					title="Remove from favorites"
					onClick={this.onUnFavoriteClick}
				>
					<StarFillIcon className="favorite-star" />
				</td>
			) : (
				<td
					className="tableRowIcon favoriteCatalogItems link button"
					title="Add to favorites"
					onClick={this.onFavoriteClick}
				>
					<StarIcon className="unfavorite-star" />
				</td>
			);
		} else if (item.ownFeature === XyiconFeature.Portfolio) {
			return (
				<td
					className={ReactUtils.cls("tableRowIcon link", {active: quickLinksActive})}
					onClick={() => openWorkspaceFromPortfolioModule(appState, item.id)}
					title="Open"
				>
					<OpenPortfolioIcon />
				</td>
			);
		} else if (item.ownFeature === XyiconFeature.Report) {
			const report = item as Report;

			return (
				<>
					<td
						className={ReactUtils.cls("tableRowIcon link", {active: quickLinksActive})}
						onClick={(e) => this.onRunReport(e, report)}
						title="Run Report"
					>
						<PlayIcon />
					</td>
					<td className="tableRowIcon reportIcon">{getReportIcon(report, this.props.appState)}</td>
				</>
			);
		}

		// Empty cell, so the sticky area on the left is the same in every row
		return <td className="tableRowIcon" />;
	}

	private getEmptyRow() {
		return <TableRowStyled className={`${this._hoverClassName} empty`} />;
	}

	public override render() {
		const {
			item,
			id,
			headers,
			index,
			numberOfItemsOnPage,
			selected,
			showCheckboxColumn,
			tableRowIcon,
			isCellContentsWrappingOn,
			appState,
			focused,
			isSimpleTable,
			hasMultipleFocusedItems,
			feature,
			onCheckboxRowClick,
			scrollTableToShowFullCell,
			colorApplicationMode,
			spotlightColor,
			fieldSpotlights,
		} = this.props;

		const {isGreyedOut, isNotAllowed, dragNdrop, isBoundarySelectorOpen, highlight} = this.state;
		const fields = this.props.fields ?? this.props.getFieldsArray?.(item, index, headers) ?? [];
		const highlightClassName = highlight === "Middle" ? this._hoverClassName : "";
		const boundarySpaceMapSelectorStyle: React.CSSProperties = {
			zIndex: 3, // Above the sticky cells
		};

		if (index <= numberOfItemsOnPage / 2) {
			boundarySpaceMapSelectorStyle.top = "0";
			boundarySpaceMapSelectorStyle.bottom = "initial";
		}

		return (
			<>
				{highlight === "Top" && this.getEmptyRow()}
				<TableRowStyled
					ref={this._trRef}
					data-key={id}
					onMouseDown={this.onMouseDown} // Option A to remove selection
					onClick={this.onClick}
					onDoubleClick={this.onDoubleClick}
					onContextMenu={this.onOpenContextMenu}
					$spotlightColor={spotlightColor}
					$colorMode={colorApplicationMode}
					style={spotlightColor && colorApplicationMode !== "field" ? ({"--spotlight-color": spotlightColor} as React.CSSProperties) : undefined}
					className={ReactUtils.cls(`${highlightClassName}`, {
						wrapped: isCellContentsWrappingOn,
						tableRowIcon,
						lower: tableRowIcon,
						report: feature === XyiconFeature.Report,
						isGrabbing: document.body.style.cursor === "grabbing",
						focused,
						selected,
						isGreyedOut,
						isNotAllowed,
						dragNdrop,
						hasSpotlightColor: !!spotlightColor,
					})}
				>
					{tableRowIcon && this.getRowIcon()}
					{showCheckboxColumn && (
						<td
							className="flexCenter sticky"
							onClick={(event) => onCheckboxRowClick(event, item)}
							onDoubleClick={Functions.stopPropagation}
							style={{height: `${this._tdRef.current?.parentElement.offsetHeight - 2}px`}}
						>
							<CheckboxInputV5
								value={selected}
								disabled={true}
							/>
						</td>
					)}
					{isBoundarySelectorOpen && (
						<BoundarySpaceMapSelectorV5
							style={boundarySpaceMapSelectorStyle}
							item={item as unknown as Boundary}
							onClose={this.onHideBoundarySpaceMaps}
						/>
					)}
					{fields.map((field, col) => {
						if (headers[col]) {
							const w = headers[col].field.includes("icon")
								? headers[col]?.width || minIconHeaderWidth
								: Math.max(headers[col]?.width, minHeaderWidth) || minHeaderWidth;
							const style: React.CSSProperties = {
								width: w,
								minWidth: w,
							};
							const fieldItem = appState.actions.getFieldByRefId(headers[col].field);
							// Check if this specific field has a spotlight rule - used for "field" or "both" colorMode
							const isSpotlightField = (colorApplicationMode === "field" || colorApplicationMode === "both") && !!fieldSpotlights?.[col];
							const cellColor = isSpotlightField ? fieldSpotlights[col].color : null;

							return (
								<OuterTableCellStyled
									style={style}
									key={isSimpleTable ? col : `${fieldItem.refId}${col}`}
									data-table-column-index={col}
									className={isSpotlightField ? "spotlight-field" : ""}
									$isSpotlightField={isSpotlightField}
									$spotlightColor={cellColor}
								>
									<TableCellV5
										field={fieldItem}
										width={w}
										columnIndex={col}
										fieldValue={isSimpleTable || !fieldItem ? field : !!field}
										isSimpleTable={isSimpleTable}
										item={item as unknown as IModel}
										hasMultipleFocusedItems={hasMultipleFocusedItems}
										getItemsToUpdate={this.getItemsToUpdate}
										scrollTableToShowFullCell={scrollTableToShowFullCell}
									/>
								</OuterTableCellStyled>
							);
						}
					})}
				</TableRowStyled>
				<PointerDetectorReact
					onHoverMove={this.onMouseMove}
					preventDefaultOnTouch={false}
					preventDefaultOnMouseMove={false}
					preventDefaultOnMouseUp={false}
					refObject={this._trRef}
				/>
				{highlight === "Bottom" && this.getEmptyRow()}
			</>
		);
	}
}

const EmbedIconWrapperStyled = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	${FlexCenterStyle};
	width: 45px;
	height: 45px;
	background-color: ${colorPalette.primary.c200Light};
	border: 2px dashed ${colorPalette.primary.c500Primary};
	color: ${colorPalette.primary.c500Primary};
`;

export const OuterTableCellStyled = styled.td<{$isSpotlightField?: boolean; $spotlightColor?: string | null}>`
	min-height: 100%;
	display: flex;
	align-self: stretch;
	position: relative;
	top: 0;
	left: 3px;
	bottom: 0;
	right: 0;
	justify-content: stretch;
	align-items: flex-start;
	outline: 2px solid transparent;

	${(props) =>
		props.$isSpotlightField &&
		props.$spotlightColor &&
		css`
			&& {
				background-color: color-mix(in srgb, ${props.$spotlightColor}, white);
				position: relative;
				z-index: 1;
			}

			&::before {
				content: "";
				position: absolute;
				left: 0;
				top: 0;
				bottom: 0;
				width: 4px;
				background-color: ${props.$spotlightColor};
				z-index: 2;
				border-radius: ${radius.md};
			}
		`}
`;

export const TableRowStyled = styled.tr<{$spotlightColor?: string; $colorMode?: SpotlightColorMode}>`
	display: flex;
	align-items: flex-start;
	position: relative;
	width: 100%;
	min-height: 40px;
	cursor: pointer;
	transition: all 0.3s ease-out;
	border: 1px solid transparent;
	border-bottom: solid 1px ${colorPalette.gray.c200Light};
	content-visibility: auto;
	contain-intrinsic-size: 40px;
	/* Always opaque, because the sticky cells inherit it to cover the cells scrolled behind them */
	background-color: ${colorPalette.white};

	${(props) =>
		props.$spotlightColor &&
		props.$colorMode !== "field" &&
		css`
			&& {
				background-color: color-mix(in srgb, ${props.$spotlightColor} 20%, white);
				position: relative;
			}

			/* The first sticky cell covers the row's stripe, so it draws its own */
			&::before,
			& > td:is(.tableRowIcon, .sticky):first-child::before {
				content: "";
				position: absolute;
				left: 0;
				top: 0;
				bottom: 0;
				width: 4px;
				background-color: ${props.$spotlightColor};
				z-index: 1;
				border-radius: ${radius.md};
			}

			&&:hover:not(.isGrabbing) {
				background-color: color-mix(in srgb, ${props.$spotlightColor} 40%, white);
			}

			&&.focused {
				background-color: color-mix(in srgb, ${props.$spotlightColor} 35%, white);
				border-color: ${colorPalette.primary.c500Primary};
				z-index: 1;
			}

			&&.selected,
			&&.focused.selected {
				background-color: color-mix(in srgb, ${props.$spotlightColor} 50%, white); /* very visible */
			}
		`}

	& > td {
		&.sticky {
			${FlexCenterStyle};
			position: sticky;
			left: 0;
			z-index: 2;
			min-height: 36px;
			align-self: stretch;
			align-items: flex-start;
			padding: 0;
			padding-top: 10px;
			background-color: inherit;
		}
	}

	&.tableRowIcon {
		& > td.sticky {
			left: ${stickyTableRowIconWidth}px;
		}

		&.report > td.sticky {
			left: ${stickyReportTableRowIconWidth}px;
		}
	}

	&.simpleTable svg {
		position: absolute;
		left: ${baseDistance.sm};
		top: ${baseDistance.sm};
	}

	&.lower {
		min-height: 40px;
	}

	.BoundarySpaceMapSelector {
		top: initial;
		right: initial;
		left: 0;
		bottom: 0;
		color: ${colorPalette.black};
	}

	&:not(.isGrabbing) {
		&:hover:not(.hasSpotlightColor) {
			background: ${colorPalette.gray.c100};

			.favoriteCatalogItems {
				box-shadow: none;
				opacity: 1;

				.unfavorite-star {
					visibility: visible;

					path {
						stroke: ${colorPalette.warning.c500Primary};
					}
				}
			}
		}

		&.hasSpotlightColor:hover {
			.favoriteCatalogItems {
				box-shadow: none;
				opacity: 1;
				z-index: 2;

				.unfavorite-star {
					visibility: visible;

					path {
						stroke: ${colorPalette.warning.c500Primary};
					}
				}
			}
		}
	}

	&.wrapped {
		td {
			&:not(.sticky, .tableRowIcon) {
				padding: 0;
			}

			&.fixed {
				${FlexCenterStyle};
				position: absolute;
				background-color: ${colorPalette.white};
				z-index: 9;
				padding: 6px;
				height: 100%;
			}

			&.boolean .ToggleSwitch {
				display: none;
			}

			&.editable {
				margin: 0;

				.textCursor {
					cursor: text;
				}

				&:focus-within {
					input {
						background-color: ${colorPalette.white};
						color: ${colorPalette.gray.c950};
						transition: none;
						margin: 0;
						padding: 5px;
						border-radius: 0;
						max-width: 100%;
						min-height: 40px;
					}
				}

				.cellContent,
				${DateTimeInputStyled} {
					border: 1px solid transparent;
					border-radius: ${radius.sm};
					min-height: 40px;

					.IconButton:hover {
						border-color: transparent;
					}

					&.XyiconModelField {
						border-color: ${colorPalette.gray.c300};
					}

					&.isActive {
						border-color: ${colorPalette.primary.c500Primary};
					}
				}

				.isOpen .modelField {
					border-color: ${colorPalette.primary.c500Primary};
					height: 32px;
					border-radius: ${radius.xs};
				}

				.MultiSelectInput {
					.list {
						.item {
							&:first-child,
							&:last-child {
								margin: 0;
							}
						}
					}
				}
			}

			.cellContent {
				white-space: pre;
				flex-grow: 1;
				min-height: 40px;

				&.overflowhidden {
					max-height: 160px;
					overflow: hidden;

					span {
						white-space: normal;
						overflow: hidden;
						display: -webkit-box;
						-webkit-line-clamp: 5;
						-webkit-box-orient: vertical;
						text-overflow: ellipsis;
					}
				}

				&.showMore {
					max-height: none;
					overflow: visible;

					span {
						display: block; // remove webkit-box to allow full flow
						-webkit-line-clamp: unset;
						-webkit-box-orient: unset;
						text-overflow: unset;
						overflow: visible;
						white-space: normal;
					}
				}

				.tdRow {
					display: block;

					&:not(:last-child):after {
						content: "";
					}
				}
			}

			&.textAlignRight {
				.cellContent {
					text-align: right;
				}
			}
		}
	}

	&.empty {
		min-height: 0px;
		height: 1px;
		display: none;
	}

	&.isGreyedOut {
		filter: grayscale(1);
	}

	&.isNotAllowed {
		border: 1px dashed #ef5350;
		background-color: #fcdddc;
	}

	&:not(.isGrabbing):hover {
		.tableRowIcon > * {
			opacity: 1;
		}

		.tableRowIcon.disabled > * {
			opacity: 0.5;
		}
	}

	td {
		${CheckboxInputStyled} {
			margin-top: 2px;
		}

		.naked.Button {
			cursor: pointer;
			background: none;

			.label {
				color: ${colorPalette.white};
			}
		}
	}

	.tableRowIcon {
		display: flex;
		align-items: center;
		justify-content: flex-start;
		align-self: stretch;
		flex-direction: column;
		padding: ${baseDistance.sm};
		position: sticky;
		left: 0;
		z-index: 2;
		width: ${stickyTableRowIconWidth}px;
		min-width: ${stickyTableRowIconWidth}px;
		background-color: inherit;
		color: ${colorPalette.primary.c500Primary};

		/* The cell itself stays opaque to cover the cells scrolled behind it, only its content is hidden until hover */
		& > * {
			opacity: 0;
			transition: opacity 0.4s ease;
		}

		&:first-child {
			/* Covers the row's left border (and draws it instead), so the cell doesn't move when the table is scrolled */
			margin-left: -1px;
			border-left: inherit;
		}

		&.active > * {
			opacity: 1;
		}

		&.disabled {
			pointer-events: auto;
		}

		&.embed {
			z-index: 3;

			& > * {
				opacity: 1;
			}
		}

		&.link {
			&:hover {
				& > svg {
					background-color: ${colorPalette.gray.c200Light};
					box-shadow: 0 0 0px 4px ${colorPalette.gray.c200Light};
					border-radius: 1px;
				}
			}

			.icon {
				fill: ${colorPalette.primary.c200Light};
			}
		}

		&.favoriteCatalogItems {
			box-shadow: none;

			& > * {
				opacity: 1;
			}

			.favorite-star path {
				display: block;
				fill: ${colorPalette.warning.c500Primary};
				stroke: ${colorPalette.warning.c500Primary};
			}

			.unfavorite-star {
				visibility: hidden;
			}
		}

		&.reportIcon {
			left: ${stickyTableRowIconWidth}px;
			width: ${stickyReportTableRowIconWidth - stickyTableRowIconWidth}px;
			min-width: ${stickyReportTableRowIconWidth - stickyTableRowIconWidth}px;
			padding: ${baseDistance.xs} ${baseDistance.sm};
			color: ${colorPalette.gray.c950};

			& > * {
				opacity: 1;
			}
		}
	}

	transition: background-color 0.4s ease;

	&.selected {
		background: ${colorPalette.primary.c200Light};
	}

	&.focused {
		background-color: ${colorPalette.gray.c200Light};
		border-color: ${colorPalette.primary.c500Primary};
		z-index: 1;

		&.selected {
			background: ${colorPalette.primary.c200Light};
		}
	}

	td {
		min-width: 40px;
		padding-left: ${baseDistance.sm};

		.cellContent {
			border: 1px solid transparent;

			&${MultiSelectInputStyled}.isActive {
				border-color: ${colorPalette.primary.c500Primary};
				background-color: ${colorPalette.white};
			}

			/* span:not(.close) {
				${ELLIPSIS}
				min-width: 100%;
			} */

			.tdRow {
				${ELLIPSIS}
				display: block;

				&:not(:last-child):after {
					content: "";
				}
			}
		}

		.naked.Button {
			width: fit-content;
			margin: 2px 0 ${baseDistance.xs} ${baseDistance.sm};
			padding: 0;
			height: 16px;

			.label {
				color: ${colorPalette.primary.c500Primary};
				font-size: ${fontSize.md};
			}
		}

		&.italic {
			font-style: italic;
			color: ${colorPalette.gray.c400};
		}
	}

	.placeholderCell {
		min-width: 40px;
	}

	${InitialsStyled} {
		.catalog-thumbnail .favourite {
			right: 9px;
		}
	}
`;
